import { useMemo, useState } from 'react';

import parse, { DOMNode, Element } from 'html-react-parser';
import { marked } from 'marked';

import { formatContent, REF_PLACEHOLDER_TAG } from './kim-message';
import { Reference } from './reference';
import { ArtifactContent, ReferenceType } from '../../../../../../../../../../../backend/src/types/types';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { cn } from '@/new/lib/utils';

export function ArtifactBlock({
  id,
  content,
  referenceIndexMap,
  onReferenceClick,
  collapsed = false,
  version,
}: {
  id: string;
  content: ArtifactContent[];
  referenceIndexMap: Map<string, { index: number; referenceType: ReferenceType }>;
  onReferenceClick?: (id: string, referenceType: ReferenceType) => void;
  collapsed?: boolean;
  version?: number;
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const isCollapsed = collapsed && !isExpanded;

  const rendered = useMemo(() => {
    if (!content?.length) return null;

    const formattedContent = formatContent(content);
    const html = marked.parse(formattedContent, { async: false }) as string;

    return parse(html, {
      replace: (domNode: DOMNode) => {
        if (domNode instanceof Element && domNode.name === REF_PLACEHOLDER_TAG) {
          const refId = domNode.attribs['data-id'];
          const refType = domNode.attribs['data-ref-type'] as ReferenceType;

          return (
            <Reference
              index={referenceIndexMap?.get(refId)?.index ?? 0}
              id={refId}
              referenceType={refType}
              onClick={
                refType === ReferenceType.Fact || refType === ReferenceType.Chunk || refType === ReferenceType.Document
                  ? onReferenceClick
                  : undefined
              }
            />
          );
        }
      },
    });
  }, [content, referenceIndexMap, onReferenceClick]);

  return (
    <div className="-mx-12 my-8 overflow-hidden rounded-lg border bg-white" {...(id && { 'data-artifact-id': id })}>
      <div className="flex items-center justify-between border-b border-slate-100 px-12 py-3">
        <div className="flex items-center gap-2">
          {version != null && <div className="text-xs text-gray-500">Version {version}</div>}
        </div>

        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="default"
            onClick={() => {
              /* TODO: wire up download handler */
            }}
            aria-label="Download artifact"
            size="sm"
          >
            <Icon className="lucide--download size-3" />
            Download
          </Button>

          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={() => {
              /* TODO: wire up publish handler */
            }}
          >
            <Icon className="lucide--users size-3" />
            Publish
          </Button>
        </div>
      </div>
      {rendered && (
        <div className="relative">
          <div className={cn('px-12 pt-2 pb-12', isCollapsed && 'max-h-28 overflow-hidden')}>
            <div className="prose prose-sm prose-headings:my-3 prose-a:hover:text-blue-600 prose-a:duration-150 prose-a:text-blue-500 prose-h1:text-2xl prose-h2:text-xl prose-h3:text-lg prose-hr:my-6 prose-p:leading-normal prose-p:text-gray-800 prose-headings:font-semibold max-w-none">
              {rendered}
            </div>
          </div>
          {isCollapsed && (
            <div
              className="absolute inset-x-0 bottom-0 flex h-20 cursor-pointer items-end justify-center bg-gradient-to-t from-white to-transparent pb-3"
              onClick={() => setIsExpanded(true)}
            >
              <span className="flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-700">
                Click to expand
                <Icon className="lucide--chevron-down size-3.5" />
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
